import { AppContext } from 'App.context';
import Colors from 'constants/Colors';
import { useState, useEffect, useContext } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import { useNavigate } from 'react-router';
import { notification } from 'antd';
import useBookingExportHook from '../../../utils/export/BookingExport.hook';
import moment from 'moment/moment';
import { guestsMock, italianGuestsMock } from 'Components/Dashboard/PMS/PMSSelector/Guest/mocks';
import useFormsHook from '../Forms/Forms.hook';
import useGuestHook from '../PMS/PMSSelector/Guest/hooks/Guest.hook';

export default function useBookingListHook() {
  const { API } = useContext(ApiContext);
  const { bookingExportActions } = useBookingExportHook();
  const navigate = useNavigate();
  const {
    appState,
    appActions: { translate, getSelectedHotel },
  } = useContext(AppContext);
  const { state: formsState } = useFormsHook();
  const { snakeToCamel } = useGuestHook();

  const [state, setState] = useState({
    isLoading: false,
    dataSource: {
      items: [],
      totalItems: 0,
      totalPages: 0,
      currentPage: 0,
      pageSize: 10,
    },
    filters: {},
    datesRange: getDefaultDateRange(),
    sort: null,
    pmsList: [],
    selectedHotelPms: null,
    hotelStatusId: null,
    showNoIntegrationControls: false,
    createBookingModal: false,
    fieldsModal: false,
    guestFieldsData: null,
    initializedMockedFields: false,
    mockedFields: null,
    editStatus: null,
    newStatus: null,
  });

  useEffect(() => {
    actions.loadBookings();
    actions.loadPmsHotel();
    actions.loadPMSList();
  }, [state.dataSource.currentPage, state.filters, appState.selectedHotel, state.datesRange]);

  useEffect(() => {
    updateState({
      showNoIntegrationControls:
        (appState.superUser || state.hotelStatusId == 6) &&
        state.selectedHotelPms === 'NoIntegration',
      initializedMockedFields: false,
    });
  }, [state.hotelStatusId, state.selectedHotelPms, appState]);

  const selectedHotel = getSelectedHotel();
  useEffect(() => {
    if (
      state.showNoIntegrationControls &&
      state.pmsList?.length &&
      selectedHotel &&
      !state.initializedMockedFields
    ) {
      actions.initializeMockFields();
    }
  }, [selectedHotel?.id, state.showNoIntegrationControls, state.pmsList, formsState.form]);

  const actions = {
    loadBookings: () => {
      updateState({ isLoading: true });
      API.bookingsRequest(
        state.dataSource?.currentPage,
        state.dataSource?.pageSize,
        state.filters,
        state.sort,
        state.datesRange,
        getSelectedHotel()
      )
        .then(res => {
          updateState({ dataSource: res, isLoading: false });
        })
        .catch(err => {
          console.warn(err);
          updateState({ isLoading: false });
        });
    },

    loadPmsHotel: () => {
      API.pmsHotelsRequest()
        .then(res => {
          updateState({ allHotels: res });
          const hotel = res.find(hotel => {
            return hotel.id === getSelectedHotel()?.id;
          });
          if (hotel) {
            actions.onSelectHotel(hotel);
          }
        })
        .catch(err => {
          console.warn(err);
        });
    },

    loadPMSList: () => {
      API.pmsListRequest()
        .then(res => {
          updateState({ pmsList: res });
        })
        .catch(err => {
          console.warn(err);
        });
    },

    getCheckinStatusColor: (total, checkedIn) => {
      if (checkedIn == 0) {
        return Colors.disableButton;
      } else if (checkedIn == total) {
        return Colors.greenPill;
      } else {
        return Colors.yellowPill;
      }
    },

    onRowClicked: (item, _index) => {
      navigate('/checkin', {
        state: { bookingId: item?.id },
      });
    },

    onChange: filters => {
      var newPage,
        newPageSize = -1;
      var searchFilters;
      var sortInfo;

      if (state.dataSource?.currentPage !== filters?.pagination.page) {
        newPage = filters?.pagination.page;
      }
      if (state.dataSource?.pageSize !== filters?.pagination.pageSize) {
        newPageSize = filters?.pagination.pageSize;
      }

      if (filters?.where) {
        const code = filters?.where['code'];
        searchFilters = { code };
      }

      if (filters?.sort) {
        const sortField =
          typeof filters.sort?.sortField === 'string'
            ? filters.sort?.sortField
            : filters.sort?.sortField?.join('.');
        const order = filters.sort?.sortOrder;
        var code;

        switch (sortField) {
          case 'total':
            code = 'total';
            break;
          case 'departure':
            code = 'departure';
            break;
          case 'hotel':
            code = 'hotel';
            break;
          case 'entrance':
            code = 'entrance';
            break;
          default:
            break;
        }

        sortInfo = code && order ? `${code} ${order}` : null;
      }

      updateState({
        dataSource: {
          ...state.dataSource,
          currentPage: newPage >= 0 ? newPage : state.dataSource?.currentPage,
          pageSize: newPageSize >= 0 ? newPageSize : state.dataSource?.pageSize,
        },
        filters: searchFilters,
        sort: sortInfo,
      });
    },

    onDateChange: (_date, dateString) => {
      updateState({ datesRange: dateString });
    },

    export: () => {
      const dateFrom = state.datesRange[0];
      const dateTo = state.datesRange[1];

      if (!dateFrom || !dateTo) {
        notification.info({
          message: 'Debe seleccionar un rango de fechas para realizar la exportación',
        });
        return;
      }
      bookingExportActions.export(getSelectedHotel()?.id, dateFrom, dateTo);
    },

    openFieldsModal: () => {
      updateState({ fieldsModal: true });
    },

    closeFieldsModal: () => {
      updateState({ fieldsModal: false });
    },

    saveGuestFieldsData: data => {
      updateState({ guestFieldsData: data });
    },

    initializeMockFields: async () => {
      const selectedPms = state.pmsList.find(pms => pms.name === state.selectedHotelPms);
      const pmsConfig = await actions.fetchPmsConfig({ selectedPms });
      const hotelConfig = await actions.fetchHotelConfig();
      actions.composeMock({ pmsConfig, hotelConfig });
    },

    fetchPmsConfig: async ({ selectedPms }) => {
      try {
        const config = await API.pmsConfigDynamicFormRequest({ pmsId: selectedPms.id });
        return config;
      } catch (error) {
        console.error('Error loading PMS config:', error);
      }
    },

    fetchHotelConfig: async () => {
      try {
        const hotelId = getSelectedHotel()?.id;
        const hotelConfig = await API.getOneHotel({ hotelId });
        return hotelConfig;
      } catch (error) {
        console.error('Error loading Hotel config:', error);
      }
    },

    onSelectHotel: async hotel => {
      if (!hotel?.id) return;

      let statusId = null;
      try {
        const hotelConfig = await actions.fetchHotelConfig();
        statusId = hotelConfig?.status_id;
      } catch (error) {
        console.error('Error fetching hotelConfig in onSelectHotel:', error);
      }

      updateState({
        selectedHotelPms: hotel?.pmsCheckin,
        hotelStatusId: statusId,
      });
    },

    composeMock: ({ pmsConfig, hotelConfig }) => {
      if (pmsConfig && hotelConfig && formsState.form) {
        const pos = getRandomInt(0, guestsMock.length - 1);
        const defaultMock = guestsMock[pos];

        let supportedFields = pmsConfig.config_dynamic_form?.supportedFields?.map(
          field => field.civitfun
        );

        if (supportedFields) {
          let newMock = defaultMock;
          if (hotelConfig.country === 'IT') {
            const pos = getRandomInt(0, italianGuestsMock.length - 1);
            newMock = italianGuestsMock[pos];
          }

          // Filter by Form
          if (formsState.isDynamicForm) {
            const formsFieldsActive = formsState.form.holder_1.filter(
              field => field.state == 'active'
            );
            const camelCaseFormsFieldsActive = formsFieldsActive.map(form =>
              snakeToCamel(form.name)
            );
            newMock.fields = newMock.fields.filter(field => {
              return camelCaseFormsFieldsActive.includes(snakeToCamel(field.key));
            });
          } else {
            const formsFieldsActive = formsState.form.filter(field => field.state == 'active');
            const camelCaseFormsFieldsActive = formsFieldsActive.map(form =>
              snakeToCamel(form.name)
            );
            newMock.fields = newMock.fields.filter(field => {
              return camelCaseFormsFieldsActive.includes(snakeToCamel(field.key));
            });
          }
          updateState({ mockedFields: newMock, initializedMockedFields: true });
        } else {
          updateState({ mockedFields: defaultMock, initializedMockedFields: true });
        }
      }
    },

    onClickCreateBooking: () => {
      updateState({ createBookingModal: true });
    },

    onCancelCreateBooking: () => {
      updateState({ createBookingModal: false });
    },

    toggleEditStatus: rowId => {
      updateState({
        editStatus: state.editStatus === rowId ? null : rowId,
      });
    },

    saveChanges: (rowId, newStatus) => {
      API.updateBookingStatus({
        hotelId: getSelectedHotel()?.id,
        bookingId: rowId,
        status: newStatus,
      })
        .then(() => {
          notification.success({
            message: translate('bookingList.successSaveState'),
          });
          updateState({ editStatus: null, newStatus: null });
          actions.loadBookings();
        })
        .catch(err => {
          notification.error({
            message: translate('bookingList.errorSaveState'),
            description: translate('bookingList.errorDetails'),
          });
        });
    },

    onBookingCreated: () => {
      updateState({ createBookingModal: false });
      actions.loadBookings();
    },
  };

  function getDefaultDateRange() {
    return [moment().subtract(1, 'day').startOf('day'), moment().subtract(0, 'day').endOf('day')];
  }

  const getRandomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
}
